import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserPlusIcon,
  XMarkIcon,
  UserCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { projectService } from '@/services/projectService';
import { userService, UserResponse } from '@/services/userService';
import { useNotificationStore } from '@/store/notificationStore';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Project } from '@/types/project';

interface ProjectMembersProps {
  project: Project;
  onClose: () => void;
}

const ROLE_OPTIONS = [
  { value: 'superadmin', label: 'Super Admin' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'developer', label: 'Developer' },
  { value: 'support_team', label: 'Support Team' },
  { value: 'project_user', label: 'User' },
];

const ROLE_COLORS: Record<string, string> = {
  superadmin: 'bg-purple-100 text-purple-700',
  project_manager: 'bg-blue-100 text-blue-700',
  project_architect: 'bg-blue-100 text-blue-700',
  developer: 'bg-green-100 text-green-700',
  support_team: 'bg-green-100 text-green-700',
  project_user: 'bg-gray-100 text-gray-700',
  viewer: 'bg-gray-100 text-gray-600',
};

export function ProjectMembers({ project, onClose }: ProjectMembersProps) {
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('developer');

  // Fetch current members
  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ['project-members', project.id],
    queryFn: () => projectService.getProjectMembers(project.id),
  });

  // Fetch all users (to show available users to add)
  const { data: allUsers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getUsers(),
  });

  // Users not already members
  const memberUserIds = new Set(members.map((m: any) => m.user_id));
  const availableUsers = allUsers.filter((u) => !memberUserIds.has(u.id));

  // Add member
  const addMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      projectService.addMember(project.id, userId, role),
    onSuccess: () => {
      addNotification({ type: 'success', title: 'Member Added', message: 'User added to project.' });
      queryClient.invalidateQueries({ queryKey: ['project-members', project.id] });
      setShowAddForm(false);
      setSelectedUserId('');
      setSelectedRole('project_user');
    },
    onError: (e: any) => {
      addNotification({ type: 'error', title: 'Error', message: e?.response?.data?.message || 'Failed to add member' });
    },
  });

  // Remove member
  const removeMutation = useMutation({
    mutationFn: (userId: string) => projectService.removeMember(project.id, userId),
    onSuccess: () => {
      addNotification({ type: 'success', title: 'Member Removed', message: 'User removed from project.' });
      queryClient.invalidateQueries({ queryKey: ['project-members', project.id] });
    },
    onError: (e: any) => {
      addNotification({ type: 'error', title: 'Error', message: e?.response?.data?.message || 'Failed to remove member' });
    },
  });

  const getUserName = (userId: string) => {
    const user = allUsers.find((u) => u.id === userId);
    return user ? user.name : 'Unknown';
  };

  const getUserEmail = (userId: string) => {
    const user = allUsers.find((u) => u.id === userId);
    return user ? user.email : '';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {project.name} — Members
          </h3>
          <p className="text-sm text-gray-500">
            {project.key} · {members.length} member{members.length !== 1 ? 's' : ''}
          </p>
        </div>
        {!showAddForm && (
          <Button
            variant="primary"
            size="sm"
            leftIcon={<UserPlusIcon className="h-4 w-4" />}
            onClick={() => setShowAddForm(true)}
          >
            Add Member
          </Button>
        )}
      </div>

      {/* Add Member Form */}
      {showAddForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-3">Add a user to this project</h4>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">Select user...</option>
              {availableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
            <select
              className="w-32 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => selectedUserId && addMutation.mutate({ userId: selectedUserId, role: selectedRole })}
                isLoading={addMutation.isPending}
                disabled={!selectedUserId}
              >
                Add
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { setShowAddForm(false); setSelectedUserId(''); }}
              >
                Cancel
              </Button>
            </div>
          </div>
          {availableUsers.length === 0 && (
            <p className="text-xs text-blue-700 mt-2">All users are already members of this project.</p>
          )}
        </div>
      )}

      {/* Members List */}
      {membersLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      ) : members.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <UserCircleIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>No members yet. Add users to this project.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {members.map((member: any) => (
            <div
              key={member.user_id || member.id}
              className="flex items-center justify-between py-3 hover:bg-gray-50 px-2 rounded transition-colors"
            >
              <div className="flex items-center gap-3">
                <UserCircleIcon className="h-9 w-9 text-gray-300" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {member.user_name || member.name || getUserName(member.user_id)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {member.user_email || member.email || getUserEmail(member.user_id)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[member.role] || ROLE_COLORS.member}`}>
                  {member.role === 'owner' && <ShieldCheckIcon className="h-3 w-3 mr-1" />}
                  {member.role}
                </span>
                <button
                  onClick={() => removeMutation.mutate(member.user_id || member.id)}
                  className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                  title="Remove member"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Close */}
      <div className="flex justify-end pt-2 border-t">
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

export default ProjectMembers;
